'use client';
import React, { useState } from 'react';
import { BookOpen, Thermometer, Cloud, ArrowUp, Zap, Wind, AlertTriangle, CheckCircle, ChevronDown, ChevronUp, HelpCircle,  } from 'lucide-react';

type TabId = 'emagram' | 'metrics' | 'safety' | 'glossary' | 'reading';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
}

const TABS: Tab[] = [
  { id: 'emagram', label: 'Lire l\'Émagramme', icon: <ArrowUp size={15} /> },
  { id: 'metrics', label: 'Les Métriques', icon: <Thermometer size={15} /> },
  { id: 'safety', label: 'Seuils de Sécurité', icon: <AlertTriangle size={15} /> },
  { id: 'glossary', label: 'Glossaire', icon: <BookOpen size={15} /> },
  { id: 'reading', label: 'Lire la Météo', icon: <Cloud size={15} /> },
];

const GLOSSARY_TERMS = [
  { id: 'gl-thermal', term: 'Thermique', definition: 'Colonne d\'air chaud ascendant créée par le réchauffement différentiel du sol. Base du vol à voile. Un thermique puissant peut atteindre 5 m/s de montée.' },
  { id: 'gl-cbh', term: 'Base des cumulus (CBH)', definition: 'Altitude à laquelle la vapeur d\'eau se condense dans un thermique, formant la base du nuage cumulus. Correspond au Niveau de Condensation par Soulèvement (LCL). En général entre 800m et 3000m selon les conditions.' },
  { id: 'gl-vz', term: 'VZ — Vitesse Verticale', definition: 'Vitesse de montée dans un thermique, exprimée en m/s ou ft/min. Une VZ de 2 m/s est correcte, 4 m/s est excellente. Valeur négative = air descendant (rabattant).' },
  { id: 'gl-dalr', term: 'DALR — Gradient Adiabatique Sec', definition: 'Taux de refroidissement d\'une bulle d\'air SEC en ascension : 1°C pour 100m de montée. Sur l\'émagramme, c\'est la ligne de référence pour évaluer l\'instabilité.' },
  { id: 'gl-inversion', term: 'Inversion thermique', definition: 'Couche où la température augmente avec l\'altitude (au lieu de diminuer). Elle agit comme un couvercle qui bloque les thermiques. Le plafond de vol est souvent limité par l\'inversion.' },
  { id: 'gl-cape', term: 'CAPE — Énergie Potentielle de Convection', definition: 'Mesure de l\'énergie disponible pour la convection. CAPE < 300 J/kg : risque orageux faible. 300–1000 J/kg : risque modéré. > 1000 J/kg : risque élevé. Valeur nulle = atmosphère stable.' },
  { id: 'gl-li', term: 'Lifted Index (LI)', definition: 'Indice d\'instabilité atmosphérique. LI > 2 : stable. LI 0 à 2 : légèrement instable. LI -2 à 0 : instable (bons thermiques). LI < -4 : très instable (risque orageux sévère).' },
  { id: 'gl-wave', term: 'Onde de montagne', definition: 'Oscillation atmosphérique créée par le vent traversant un relief. Permet des vols très haute altitude (jusqu\'à FL400+). Nécessite un vent fort et régulier perpendiculaire à la chaîne.' },
  { id: 'gl-convergence', term: 'Convergence / Confluence', definition: 'Zone où deux masses d\'air se rejoignent et forcent l\'air à monter. Crée des lignes de thermiques alignés, idéales pour le vol de distance. Visible comme une ligne de cumulus bien développés.' },
  { id: 'gl-bl', term: 'Couche limite (BL)', definition: 'Partie de l\'atmosphère directement influencée par le sol. Son épaisseur détermine le plafond thermique maximal. En été, peut atteindre 3000–4000m en plaine.' },
  { id: 'gl-trigger', term: 'Température de déclenchement', definition: 'Température que le sol doit atteindre pour que les premières bulles thermiques se détachent. Calculée à partir du sondage matinal. Quand le thermomètre au sol dépasse cette valeur, les thermiques démarrent.' },
  { id: 'gl-lcl', term: 'LCL — Niveau de Condensation par Soulèvement', definition: 'Altitude à laquelle une bulle d\'air ascendante devient saturée et forme un nuage. Calculé par : LCL (m) ≈ 125 × (T - Td) où T est la température et Td le point de rosée au sol.' },
];

const SAFETY_THRESHOLDS = [
  {
    id: 'safe-wind',
    category: 'Vent',
    icon: <Wind size={16} />,
    thresholds: [
      { level: 'ok', label: '< 15 kt', description: 'Conditions idéales pour débutants', color: '#34d399' },
      { level: 'caution', label: '15–25 kt', description: 'Acceptable, turbulences possibles', color: '#fbbf24' },
      { level: 'warning', label: '25–35 kt', description: 'Pilotes expérimentés uniquement', color: '#f87171' },
      { level: 'danger', label: '> 35 kt', description: 'Ne pas voler', color: '#a78bfa' },
    ],
  },
  {
    id: 'safe-storm',
    category: 'Risque Orageux',
    icon: <Zap size={16} />,
    thresholds: [
      { level: 'ok', label: '< 20%', description: 'Pas de risque significatif', color: '#34d399' },
      { level: 'caution', label: '20–45%', description: 'Surveiller l\'évolution, rentrer avant 14h', color: '#fbbf24' },
      { level: 'warning', label: '45–70%', description: 'Vol déconseillé l\'après-midi', color: '#f87171' },
      { level: 'danger', label: '> 70%', description: 'Ne pas voler', color: '#a78bfa' },
    ],
  },
  {
    id: 'safe-vz',
    category: 'Vitesse Verticale',
    icon: <ArrowUp size={16} />,
    thresholds: [
      { level: 'ok', label: '< 1 m/s', description: 'Thermiques faibles, vol difficile', color: 'var(--text-muted)' },
      { level: 'caution', label: '1–2 m/s', description: 'Thermiques modérés, vol possible', color: '#fbbf24' },
      { level: 'warning', label: '2–4 m/s', description: 'Bons thermiques, excellentes conditions', color: '#34d399' },
      { level: 'danger', label: '> 5 m/s', description: 'Turbulences fortes, prudence', color: '#f87171' },
    ],
  },
  {
    id: 'safe-cape',
    category: 'CAPE',
    icon: <Zap size={16} />,
    thresholds: [
      { level: 'ok', label: '0–300 J/kg', description: 'Stable à légèrement instable', color: '#34d399' },
      { level: 'caution', label: '300–1000 J/kg', description: 'Risque orageux modéré', color: '#fbbf24' },
      { level: 'warning', label: '1000–2500 J/kg', description: 'Risque orageux élevé', color: '#f87171' },
      { level: 'danger', label: '> 2500 J/kg', description: 'Risque orageux extrême — NE PAS VOLER', color: '#a78bfa' },
    ],
  },
];

const METRICS_EXPLAINED = [
  {
    id: 'met-cbh',
    icon: <Cloud size={18} />,
    title: 'Base des Cumulus',
    color: '#a78bfa',
    shortDesc: 'Altitude du bas des nuages cumulus',
    fullDesc: 'La base des cumulus détermine jusqu\'où vous pouvez monter avec un thermique. Une base à 2000m signifie que vous pouvez potentiellement atteindre 2000m d\'altitude absolue en restant dans le thermique. En dessous de 800m, les conditions sont difficiles.',
    goodValue: '> 1500m',
    marginalValue: '800–1500m',
    badValue: '< 800m',
    formula: 'LCL (m) ≈ 125 × (T sol - Td sol)',
  },
  {
    id: 'met-vz',
    icon: <ArrowUp size={18} />,
    title: 'Vitesse Verticale (VZ)',
    color: '#00d4ff',
    shortDesc: 'Force des thermiques en m/s',
    fullDesc: 'La VZ indique la force des thermiques. Avec une VZ de 2 m/s, vous montez de 120m par minute. À 4 m/s, vous montez de 240m/min. C\'est la métrique clé pour évaluer si vous pouvez maintenir l\'altitude ou rallier un terrain de secours.',
    goodValue: '> 2.5 m/s',
    marginalValue: '1–2.5 m/s',
    badValue: '< 1 m/s',
    formula: 'Calculé par modèle thermique (BL depth + instabilité)',
  },
  {
    id: 'met-trigger',
    icon: <Thermometer size={18} />,
    title: 'Température de Déclenchement',
    color: '#fbbf24',
    shortDesc: 'Temp. sol pour démarrer les thermiques',
    fullDesc: 'La température de déclenchement est la température minimale que le sol doit atteindre pour que les premières bulles thermiques se détachent. Si la température max prévue est inférieure à cette valeur, il n\'y aura pas de thermiques dans la journée.',
    goodValue: 'Temp max > déclenchement',
    marginalValue: 'Temp max ≈ déclenchement',
    badValue: 'Temp max < déclenchement',
    formula: 'Lue sur l\'émagramme du matin (sondage 00Z ou 06Z)',
  },
  {
    id: 'met-li',
    icon: <Zap size={18} />,
    title: 'Lifted Index (LI)',
    color: '#f87171',
    shortDesc: 'Indice d\'instabilité (risque orage)',
    fullDesc: 'Le Lifted Index mesure la différence de température entre une bulle d\'air soulevée et l\'atmosphère à 500 hPa. Un LI négatif signifie que la bulle est plus chaude que l\'environnement = instabilité = thermiques et risque d\'orages si trop négatif.',
    goodValue: 'LI entre -1 et -3',
    marginalValue: 'LI entre -3 et -5',
    badValue: 'LI < -5 (risque orageux)',
    formula: 'LI = T env(500hPa) - T bulle soulevée(500hPa)',
  },
];

function GlossaryItem({ term }: { term: typeof GLOSSARY_TERMS[0] }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-200"
      style={{ border: `1px solid ${open ? 'rgba(0,212,255,0.25)' : 'var(--border)'}` }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/5 transition-colors"
      >
        <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{term.term}</span>
        {open
          ? <ChevronUp size={14} style={{ color: 'var(--text-muted)' }} />
          : <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />
        }
      </button>
      {open && (
        <div className="px-4 pb-4 animate-fade-in">
          <p className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>{term.definition}</p>
        </div>
      )}
    </div>
  );
}

function EmagramGuideTab() {
  return (
    <div className="space-y-6">
      <div
        className="rounded-xl p-5"
        style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}
      >
        <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
          Qu'est-ce qu'un émagramme ?
        </h3>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
          Un émagramme (ou sondage atmosphérique) représente la température et l'humidité de l'atmosphère
          à différentes altitudes, mesurées par un radiosondage (ballon météo). Pour le planeur,
          c'est l'outil fondamental pour prévoir la qualité des thermiques avant de décoller.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          {
            id: 'step-1',
            step: '1',
            title: 'La courbe rouge — Température',
            color: '#f87171',
            text: 'Représente la température réelle de l\'atmosphère à chaque altitude. Plus elle est à droite, plus il fait chaud. Si cette courbe est très verticale (se refroidit vite avec l\'altitude), l\'atmosphère est instable et les thermiques seront forts.',
          },
          {
            id: 'step-2',
            step: '2',
            title: 'La courbe verte — Point de rosée',
            color: '#34d399',
            text: 'Représente l\'humidité de l\'air. Quand la courbe de température rejoint la courbe de point de rosée, l\'air est saturé et un nuage se forme. L\'écart entre les deux au sol (spread T/Td) détermine la hauteur de la base des cumulus.',
          },
          {
            id: 'step-3',
            step: '3',
            title: 'La ligne jaune — DALR',
            color: '#fbbf24',
            text: 'Le Gradient Adiabatique Sec (1°C/100m) est la ligne de référence. Si la courbe de température est à droite de cette ligne, l\'air est instable et les thermiques montent. Si elle est à gauche, l\'air est stable et les thermiques s\'arrêtent.',
          },
          {
            id: 'step-4',
            step: '4',
            title: 'Le plafond thermique',
            color: '#a78bfa',
            text: 'C\'est l\'altitude où la courbe de température croise la ligne DALR (partant de la surface). Au-dessus de ce point, les bulles thermiques ne sont plus plus chaudes que l\'air ambiant et s\'arrêtent. C\'est votre plafond de vol en thermique.',
          },
        ].map((step) => (
          <div
            key={step.id}
            className="rounded-xl p-4"
            style={{ backgroundColor: 'var(--surface)', border: `1px solid ${step.color}25` }}
          >
            <div className="flex items-center gap-3 mb-2">
              <span
                className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                style={{ backgroundColor: `${step.color}20`, color: step.color }}
              >
                {step.step}
              </span>
              <h4 className="text-sm font-semibold" style={{ color: step.color }}>{step.title}</h4>
            </div>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>{step.text}</p>
          </div>
        ))}
      </div>

      <div
        className="rounded-xl p-5"
        style={{ backgroundColor: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.2)' }}
      >
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle size={16} style={{ color: '#34d399' }} />
          <h4 className="text-sm font-semibold" style={{ color: '#34d399' }}>Comment utiliser l'émagramme SoarCast</h4>
        </div>
        <ol className="space-y-2">
          {[
            'Regardez le sondage du matin (06h) pour évaluer la stabilité initiale',
            'Faites glisser le curseur vers 10h-11h pour voir quand les thermiques démarrent (DALR croise la courbe temp)',
            'À 13h-14h, le plafond thermique est généralement au maximum — notez cette altitude',
            'Observez si une inversion est présente (courbe temp qui remonte = couvercle sur les thermiques)',
            'Vérifiez l\'écart T/Td au sol pour estimer la base des cumulus',
          ].map((step, i) => (
            <li key={`how-${i}`} className="flex items-start gap-2.5 text-sm" style={{ color: 'var(--text-muted)' }}>
              <span
                className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5"
                style={{ backgroundColor: 'rgba(52,211,153,0.15)', color: '#34d399' }}
              >
                {i + 1}
              </span>
              {step}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function MetricsTab() {
  const [expandedMetric, setExpandedMetric] = useState<string | null>('met-cbh');

  return (
    <div className="space-y-4">
      {METRICS_EXPLAINED.map((metric) => (
        <div
          key={metric.id}
          className="rounded-xl overflow-hidden transition-all duration-200"
          style={{
            backgroundColor: 'var(--surface)',
            border: `1px solid ${expandedMetric === metric.id ? `${metric.color}35` : 'var(--border)'}`,
          }}
        >
          <button
            onClick={() => setExpandedMetric(expandedMetric === metric.id ? null : metric.id)}
            className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-white/3 transition-colors"
          >
            <span style={{ color: metric.color }}>{metric.icon}</span>
            <div className="flex-1">
              <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{metric.title}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{metric.shortDesc}</p>
            </div>
            <div className="flex items-center gap-3">
              <span
                className="text-xs px-2.5 py-1 rounded-full font-medium"
                style={{ backgroundColor: `${metric.color}18`, color: metric.color }}
              >
                {metric.goodValue}
              </span>
              {expandedMetric === metric.id
                ? <ChevronUp size={15} style={{ color: 'var(--text-muted)' }} />
                : <ChevronDown size={15} style={{ color: 'var(--text-muted)' }} />
              }
            </div>
          </button>

          {expandedMetric === metric.id && (
            <div className="px-5 pb-5 animate-fade-in" style={{ borderTop: '1px solid var(--border)' }}>
              <p className="text-sm leading-relaxed mt-4 mb-4" style={{ color: 'var(--text-muted)' }}>{metric.fullDesc}</p>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[
                  { label: 'Favorable', value: metric.goodValue, color: '#34d399' },
                  { label: 'Marginal', value: metric.marginalValue, color: '#fbbf24' },
                  { label: 'Défavorable', value: metric.badValue, color: '#f87171' },
                ].map((thresh) => (
                  <div
                    key={`thresh-${metric.id}-${thresh.label}`}
                    className="rounded-lg p-3 text-center"
                    style={{ backgroundColor: `${thresh.color}10`, border: `1px solid ${thresh.color}25` }}
                  >
                    <p className="text-xs mb-1" style={{ color: thresh.color }}>{thresh.label}</p>
                    <p className="font-mono-data text-xs font-semibold" style={{ color: thresh.color }}>{thresh.value}</p>
                  </div>
                ))}
              </div>
              <div
                className="flex items-start gap-2 px-3 py-2.5 rounded-lg text-xs"
                style={{ backgroundColor: 'var(--surface-elevated)', border: '1px solid var(--border)' }}
              >
                <span className="font-mono-data" style={{ color: 'var(--text-muted)' }}>f(x)</span>
                <span className="font-mono-data text-xs" style={{ color: 'var(--text-muted)' }}>{metric.formula}</span>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function SafetyTab() {
  return (
    <div className="space-y-6">
      <div
        className="flex items-start gap-3 px-4 py-4 rounded-xl"
        style={{ backgroundColor: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)' }}
      >
        <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" style={{ color: '#f87171' }} />
        <div>
          <p className="text-sm font-semibold mb-1" style={{ color: '#f87171' }}>Rappel de Sécurité</p>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
            Ces seuils sont indicatifs et ne remplacent pas la formation, le briefing météo officiel,
            et le jugement du commandant de bord. En cas de doute, ne volez pas. La météo peut évoluer
            rapidement — restez toujours à portée de votre terrain ou d'un terrain de dégagement.
          </p>
        </div>
      </div>

      {SAFETY_THRESHOLDS.map((cat) => (
        <div
          key={cat.id}
          className="rounded-xl p-5"
          style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}
        >
          <div className="flex items-center gap-2 mb-4">
            <span style={{ color: '#00d4ff' }}>{cat.icon}</span>
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{cat.category}</h3>
          </div>
          <div className="space-y-2">
            {cat.thresholds.map((thresh) => (
              <div
                key={`safety-${cat.id}-${thresh.level}`}
                className="flex items-center gap-4 px-4 py-3 rounded-lg"
                style={{ backgroundColor: `${thresh.color}08`, border: `1px solid ${thresh.color}20` }}
              >
                <span
                  className="font-mono-data text-sm font-bold w-24 flex-shrink-0"
                  style={{ color: thresh.color }}
                >
                  {thresh.label}
                </span>
                <p className="text-sm flex-1" style={{ color: 'var(--text-muted)' }}>{thresh.description}</p>
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: thresh.color }}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function GlossaryTab() {
  const [search, setSearch] = useState('');
  const filtered = GLOSSARY_TERMS.filter(
    t => t.term.toLowerCase().includes(search.toLowerCase()) ||
         t.definition.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-3">
      <div
        className="flex items-center gap-3 px-4 py-2.5 rounded-xl mb-4"
        style={{ backgroundColor: 'var(--surface-elevated)', border: '1px solid var(--border)' }}
      >
        <HelpCircle size={15} style={{ color: 'var(--text-muted)' }} />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un terme…"
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-[var(--text-muted)]"
          style={{ color: 'var(--text-primary)' }}
        />
      </div>
      {filtered.length === 0 && (
        <div className="text-center py-8">
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aucun terme trouvé pour "{search}"</p>
        </div>
      )}
      {filtered.map((term) => (
        <GlossaryItem key={term.id} term={term} />
      ))}
    </div>
  );
}

function ReadingWeatherTab() {
  return (
    <div className="space-y-6">
      <div
        className="rounded-xl p-5"
        style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}
      >
        <h3 className="text-base font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
          Routine météo du matin — Checklist
        </h3>
        <div className="space-y-2">
          {[
            { id: 'chk-1', step: 'Vérifier le verdict SoarCast', detail: 'Favorable / Marginal / Non favorable — c\'est votre première décision', color: '#34d399' },
            { id: 'chk-2', step: 'Lire l\'émagramme 06Z', detail: 'Identifier la température de déclenchement et l\'heure prévue de début de convection', color: '#00d4ff' },
            { id: 'chk-3', step: 'Vérifier la base des cumulus', detail: 'Assez haute pour voler en sécurité ? (minimum recommandé : 800m)', color: '#a78bfa' },
            { id: 'chk-4', step: 'Évaluer le risque orageux', detail: 'CAPE, LI, heure de déclenchement orageux possible — planifier le retour', color: '#fbbf24' },
            { id: 'chk-5', step: 'Analyser le vent en altitude', detail: 'Vent fort en altitude = turbulences. Vent orienté = onde possible', color: 'var(--text-muted)' },
            { id: 'chk-6', step: 'Vérifier la convergence', detail: 'Une ligne de convergence = vol de distance facilité sur cet axe', color: '#fbbf24' },
            { id: 'chk-7', step: 'Consulter le METAR / TAF', detail: 'Données officielles du terrain — vitales pour la décision finale', color: '#f87171' },
          ].map((item, i) => (
            <div
              key={item.id}
              className="flex items-start gap-3 px-4 py-3 rounded-lg"
              style={{ backgroundColor: 'var(--surface-elevated)', border: '1px solid var(--border)' }}
            >
              <span
                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5"
                style={{ backgroundColor: `${item.color}18`, color: item.color }}
              >
                {i + 1}
              </span>
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{item.step}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{item.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          className="rounded-xl p-5"
          style={{ backgroundColor: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.2)' }}
        >
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle size={15} style={{ color: '#34d399' }} />
            <h4 className="text-sm font-semibold" style={{ color: '#34d399' }}>Journée idéale</h4>
          </div>
          <ul className="space-y-1.5">
            {[
              'Température sol dépasse le déclenchement à 10h',
              'Base cumulus > 1500m',
              'VZ > 2.5 m/s prévue',
              'CAPE < 300 J/kg',
              'Vent sol < 15 kt',
              'Convergence présente sur l\'axe de vol',
            ].map((item, i) => (
              <li key={`good-${i}`} className="flex items-start gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                <span style={{ color: '#34d399' }}>✓</span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div
          className="rounded-xl p-5"
          style={{ backgroundColor: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.2)' }}
        >
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={15} style={{ color: '#f87171' }} />
            <h4 className="text-sm font-semibold" style={{ color: '#f87171' }}>Signaux d'alarme</h4>
          </div>
          <ul className="space-y-1.5">
            {[
              'CAPE > 1000 J/kg — risque orageux élevé',
              'LI < -4 — atmosphère très instable',
              'Base Cu < 600m — plafond très bas',
              'Vent sol > 25 kt — turbulences sévères',
              'Nuages Cb ou TCU sur l\'axe de vol',
              'Température ne dépasse pas le déclenchement',
            ].map((item, i) => (
              <li key={`bad-${i}`} className="flex items-start gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                <span style={{ color: '#f87171' }}>✗</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function GuideContent() {
  const [activeTab, setActiveTab] = useState<TabId>('emagram');

  return (
    <div className="min-h-screen px-4 md:px-6 xl:px-8 2xl:px-10 py-6 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Guide du Débutant</h1>
        <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
          Comprendre la météo pour le vol à voile — de l'émagramme aux décisions de sécurité
        </p>
      </div>

      {/* Tab bar */}
      <div
        className="flex gap-1 p-1 rounded-xl mb-6 overflow-x-auto"
        style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}
      >
        {TABS.map((tab) => (
          <button
            key={`guide-tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 whitespace-nowrap flex-shrink-0 active:scale-95"
            style={{
              backgroundColor: activeTab === tab.id ? 'rgba(0,212,255,0.12)' : 'transparent',
              color: activeTab === tab.id ? '#00d4ff' : 'var(--text-muted)',
              border: activeTab === tab.id ? '1px solid rgba(0,212,255,0.25)' : '1px solid transparent',
            }}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="animate-fade-in">
        {activeTab === 'emagram' && <EmagramGuideTab />}
        {activeTab === 'metrics' && <MetricsTab />}
        {activeTab === 'safety' && <SafetyTab />}
        {activeTab === 'glossary' && <GlossaryTab />}
        {activeTab === 'reading' && <ReadingWeatherTab />}
      </div>
    </div>
  );
}
